/** @jsxImportSource @emotion/react */
import React, { FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { subscribeToPush } from '../../services/push';
import { registerPushDevice } from '../../api/transactions/transactions.api';

import { GuestTransactionStatus } from '../../api/transactions/types';

import { ChargeProcess } from './steps/charge-process/charge-process.component';
import { ChargeFinished } from './steps/charge-finished/charge-finished.component';
import { ChargeFail } from './steps/charge-fail/charge-fail.component';
import { ChargeWait } from './steps/charge-wait/charge-wait.component';
import { PaymentWait } from './steps/payment-wait/payment-wait.component';
import MenuBlock from '../../components/menu-block/menu-block';
import { Fade, Modal, useMediaQuery } from '@mui/material';
import chargeStyles from './charge.styles';
import flageInbound from '../../assets/flage_inbound.svg';
import crossIcon from '../../assets/cross.svg';
import { PoweredBy } from '../layouts/components/powered-by/powered-by.component';
import StepLoader from './steps/step-loader/step-loader.components';
import { useTransaction } from '../../hooks/useTransaction';
import { useStationSocket } from '../../hooks/useStationSocket';
import { useTransactionHistory } from '../../hooks/useTransactionHistory';

export const Charge: FC = () => {
    const isDesktop = useMediaQuery('(min-width:600px)');
    const navigate = useNavigate();
    const { guestId } = useParams();
    const { commercialTransaction, isCommercialTransactionLoading, commercialTransactionError } =
        useTransaction(guestId);
    const { addTransactionToHistory, cleanUpRecentTransaction } = useTransactionHistory();
    const { station: stationData } = useStationSocket(commercialTransaction?.station?._id);

    const isTransactionFinished =
        Object.keys(commercialTransaction?.transaction ?? {}).length &&
        commercialTransaction?.transaction?.status === 1;

    const renderContent = () => {
        if (!commercialTransaction?._id && isCommercialTransactionLoading) {
            return (
                <div css={chargeStyles.loaderContainer}>
                    <StepLoader />
                </div>
            );
        }

        if (commercialTransactionError) {
            navigate('/');
            return null;
        }

        const hasCharge = Object.keys(commercialTransaction?.transaction ?? {}).length;

        if (
            (!hasCharge &&
                (commercialTransaction?.status === GuestTransactionStatus.Canceled ||
                    commercialTransaction?.status === GuestTransactionStatus.RefundPending)) ||
            (commercialTransactionError as any)?.message == 'fail'
        ) {
            return <ChargeFail />;
        }
        if (
            hasCharge &&
            commercialTransaction &&
            (commercialTransaction?.transaction?.status !== 1 || commercialTransaction?.status === GuestTransactionStatus.Finishing)
        ) {
            return <ChargeProcess guest={commercialTransaction} />;
        }
        if (
            hasCharge &&
            commercialTransaction &&
            (commercialTransaction?.status === GuestTransactionStatus.Completed ||
                commercialTransaction?.status === GuestTransactionStatus.PaymentFailed) &&
            commercialTransaction?.transaction?.status === 1
        ) {
            return <ChargeFinished guest={commercialTransaction} />;
        }
        if (!hasCharge && commercialTransaction?.status === GuestTransactionStatus.PaymentPending) {
            return <PaymentWait />;
        }
        return <ChargeWait guest={commercialTransaction} />;
    };

    useEffect(() => {
        if (commercialTransaction?.transaction?.status === 1) {
            addTransactionToHistory(commercialTransaction);
            cleanUpRecentTransaction();
        }
    }, [commercialTransaction?.transaction?.status]);

    useEffect(() => {
        if (!guestId) return;

        const timer = setTimeout(() => {
            subscribeToPush(fid => {
                registerPushDevice(guestId, fid).catch(err => console.error(err));
            }).catch(err => console.error(err));
        }, 3000);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Modal
            sx={{
                '& 	.MuiModal-backdrop': {
                    backgroundColor: isDesktop ? '#15457580' : '#ffffff',
                    backdropFilter: 'blur(50px)',
                    overflowY: 'auto',
                },
                '&': {
                    zIndex: 10001,
                },
            }}
            open={true}
            slotProps={{
                backdrop: {
                    timeout: 300,
                },
            }}
            closeAfterTransition
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description">
            <Fade appear in>
                <div css={chargeStyles.modalContainer}>
                    {isDesktop ? (
                        <MenuBlock station={stationData?.data} style={{ zIndex: 10002 }} />
                    ) : (
                        <div css={chargeStyles.collapseBlock}>
                            <div css={chargeStyles.stationInfo}>
                                {stationData?.data?.chargePointSerialNumber ??
                                    stationData?.data?.identity}
                            </div>
                            <div>
                                <img
                                    onClick={() => navigate('/')}
                                    src={isTransactionFinished ? crossIcon : flageInbound}
                                    alt="inbound"
                                />
                            </div>
                        </div>
                    )}
                    <div css={chargeStyles.modalContentBlock}>
                        <>{renderContent()}</>
                        <div css={chargeStyles.modalContentBlockFooter}>
                            <PoweredBy />
                        </div>
                    </div>
                </div>
            </Fade>
        </Modal>
    );
};
